'use client';

import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import styles from './VisitationChart.module.scss';

type DailyEntry = { name: string; value: number; label: string };
type MonthlyEntry = { name: string; value: number; label: string };

type Props = {
  view: 'daily' | 'monthly';
  dailyData: DailyEntry[];
  monthlyData: MonthlyEntry[];
  xAxisTicks: string[];
  activeHourIdx: number;
  activeMonthIdx: number;
  chartHeight?: number;
  maxDailyValue?: number;
  maxMonthlyValue?: number;
  onBarInteract?: (index: number) => void;
  onBarSelect?: (index: number) => void;
};

const ACTIVE_FILL = 'var(--dataviz-heatmaps-5, rgba(113, 57, 249, 0.80))';
const DEFAULT_FILL = 'var(--dataviz-heatmaps-3, rgba(231, 83, 253, 0.60))';

const X_AXIS_HEIGHT = 28;
const TOOLTIP_BOTTOM = 33;
const BARS_AREA_TOP = 48;

function XAxisTick({
  x,
  y,
  width,
  payload,
  index,
  visibleTicksCount
}: {
  x?: number;
  y?: number;
  width?: number;
  payload?: { value: string };
  index?: number;
  visibleTicksCount?: number;
}) {
  const isFirst = index === 0;
  const isLast = index === (visibleTicksCount ?? 0) - 1;
  const adjustedX = isFirst ? 0 : isLast ? width : x;
  const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
  return (
    <text x={adjustedX} y={y} dy={12} textAnchor={anchor} className={styles.xAxisTick}>
      {payload?.value}
    </text>
  );
}

function MonthlyXAxisTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  return (
    <text x={x} y={y} dy={12} textAnchor="middle" className={styles.xAxisTick}>
      {payload?.value.charAt(0)}
    </text>
  );
}

export default function VisitationChart({
  view,
  dailyData,
  monthlyData,
  xAxisTicks,
  activeHourIdx,
  activeMonthIdx,
  chartHeight = 164,
  maxDailyValue,
  maxMonthlyValue,
  onBarInteract,
  onBarSelect
}: Props) {
  const barsAreaHeight = chartHeight - X_AXIS_HEIGHT;
  const data = view === 'daily' ? dailyData : monthlyData;
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [lockedIdx, setLockedIdx] = useState<number | null>(null);

  useEffect(() => {
    setLockedIdx(null);
    setHoveredIdx(null);
  }, [view]);

  const displayIdx = hoveredIdx ?? lockedIdx ?? (view === 'daily' ? activeHourIdx : activeMonthIdx);
  const activeEntry = (view === 'daily' ? dailyData[displayIdx] : monthlyData[displayIdx]) ?? null;

  const tooltipLeft = `${((2 * displayIdx + 1) / (2 * data.length)) * 100}%`;

  const rangeMax = view === 'daily' ? maxDailyValue : maxMonthlyValue;
  const maxValue = rangeMax || Math.max(...data.map(d => d.value)) || 1;
  const chartData = data.map(d => ({ ...d, value: Math.max(d.value, maxValue * 0.1) }));
  const activeValue = activeEntry?.value ?? 0;
  const activeChartValue = Math.max(activeValue, maxValue * 0.1);
  const barHeight = Math.max((activeChartValue / maxValue) * barsAreaHeight, 4);
  const dashedLineHeight = BARS_AREA_TOP + (barsAreaHeight - barHeight) - TOOLTIP_BOTTOM;

  const graphAreaRef = useRef<HTMLDivElement>(null);
  const tooltipWrapRef = useRef<HTMLDivElement>(null);
  const [tooltipShift, setTooltipShift] = useState(0);

  useLayoutEffect(() => {
    if (!activeEntry || !graphAreaRef.current || !tooltipWrapRef.current) {
      setTooltipShift(0);
      return;
    }
    const containerW = graphAreaRef.current.offsetWidth;
    const tipW = tooltipWrapRef.current.offsetWidth;
    const half = tipW / 2;
    const barCx = ((2 * displayIdx + 1) / (2 * data.length)) * containerW;
    const overflowLeft = Math.max(0, half - barCx);
    const overflowRight = Math.max(0, barCx + half - containerW);
    setTooltipShift(overflowLeft - overflowRight);
  }, [activeEntry, displayIdx, data.length]);

  const xTicks = view === 'daily' ? xAxisTicks : monthlyData.map(m => m.name);

  return (
    <div
      className={styles.graphArea}
      ref={graphAreaRef}
      style={{ '--bars-area-top': `${BARS_AREA_TOP}px`, '--tooltip-bottom': `${TOOLTIP_BOTTOM}px` } as CSSProperties}
    >
      {activeEntry && (
        <>
          <div className={styles.dashedLine} style={{ left: tooltipLeft, height: dashedLineHeight }} />
          <div
            ref={tooltipWrapRef}
            className={styles.tooltipWrap}
            style={{ left: tooltipLeft, transform: `translateX(calc(-50% + ${tooltipShift}px))` }}
          >
            <div className={styles.tooltip}>{`${activeEntry.name}: ${activeEntry.label}`}</div>
          </div>
        </>
      )}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barCategoryGap={0} onMouseLeave={() => setHoveredIdx(null)}>
          <YAxis domain={[0, maxValue]} hide />
          <ReferenceLine y={maxValue / 2} stroke="var(--color-border-separator)" strokeDasharray="2 4" strokeWidth={1} />
          <ReferenceLine y={maxValue} stroke="var(--color-border-separator)" strokeDasharray="2 4" strokeWidth={2} />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            ticks={xTicks}
            interval={0}
            tick={view === 'daily' ? <XAxisTick /> : <MonthlyXAxisTick />}
            height={28}
          />
          <Bar
            dataKey="value"
            barSize={16}
            radius={[999, 999, 999, 999]}
            isAnimationActive={false}
            cursor="pointer"
            onMouseEnter={(_, index) => { setHoveredIdx(index); onBarInteract?.(index); }}
            onClick={(_, index) => { setLockedIdx(prev => (prev === index ? null : index)); onBarInteract?.(index); onBarSelect?.(index); }}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={i === displayIdx ? ACTIVE_FILL : DEFAULT_FILL} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
